/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Breakpoint, useMediaQuery, useTheme } from "@mui/material";
import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import {
  FeatureFlagKey,
  useFeatureFlag,
} from "../../services/firebase/remote-config/useFeatureFlag";
import { RootState } from "../../store/rootReducer";

type GateProps = {
  enabled: boolean;
  redirectTo: string;
  children?: ReactNode;
};

export const Gate: FC<GateProps> = ({ enabled, redirectTo, children }) =>
  enabled ? <>{children ?? <Outlet />}</> : <Navigate to={redirectTo} replace />;

type FlagGateProps = {
  flag: FeatureFlagKey;
  redirectTo: string;
  children?: ReactNode;
};

export const FlagGate: FC<FlagGateProps> = ({ flag, redirectTo, children }) => (
  <Gate enabled={useFeatureFlag(flag)} redirectTo={redirectTo}>
    {children}
  </Gate>
);

type FlagSwitchGateProps = {
  flag: FeatureFlagKey;
  flagEnabled: ReactNode;
  flagDisabled: ReactNode;
};

export const FlagSwitchGate: FC<FlagSwitchGateProps> = ({ flag, flagEnabled, flagDisabled }) =>
  useFeatureFlag(flag) ? flagEnabled : flagDisabled;

type SelectorGateProps = {
  selector: (state: RootState) => unknown;
  redirectTo: string;
  children?: ReactNode;
};

export const SelectorGate: FC<SelectorGateProps> = ({ selector, redirectTo, children }) => (
  <Gate enabled={Boolean(useSelector(selector))} redirectTo={redirectTo}>
    {children}
  </Gate>
);

type BreakpointGateProps = {
  breakpoint?: Breakpoint;
  redirectTo: string;
  children?: ReactNode;
};

export const BreakpointGate: FC<BreakpointGateProps> = ({
  breakpoint = "md",
  redirectTo,
  children,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(breakpoint));

  return (
    <Gate enabled={isNarrow} redirectTo={redirectTo}>
      {children}
    </Gate>
  );
};
